import { getSearchAutocomplete, type GetSearchAutocompleteItemResponse, type ProjectImagePriority } from '@/api';
import { type AutocompleteEntity, type AutocompleteResult, type AutocompleteSuggestions, type GeoCoordinates, type Location, type LocationKind } from '@/views/search/types.ts';
import { Constants } from '@/utilities/constants.ts';

export async function fetchSearchAutocomplete(text: string, location: GeoCoordinates | undefined, locationSuggestionLimit: number): Promise<AutocompleteSuggestions> {
  let locationStr;
  if (location) {
    locationStr = `${location.latitude},${location.longitude}`;
  }
  const fetch = await getSearchAutocomplete({
    query: {
      search: text,
      location: locationStr,
      includeBuilders: true,
      includeProjects: true,
    },
  });
  if (fetch.response?.status == 200 && fetch.data) {
    return buildAutocompleteSuggestions(fetch.data.items, locationSuggestionLimit);
  } else {
    throw Error(`Autocomplete Failed: ${fetch.error}`);
  }
}

function buildAutocompleteSuggestions(responses: GetSearchAutocompleteItemResponse[], locationSuggestionLimit: number): AutocompleteSuggestions {
  const locations: AutocompleteResult[] = [];
  const projects: AutocompleteEntity[] = [];
  const builders: AutocompleteEntity[] = [];

  responses.forEach((response) => {
    if (response.type === 'project') {
      if (response.projectId != null) {
        projects.push(toEntity('project', response.projectId, response));
      }
      return;
    }

    if (response.type === 'builder') {
      if (response.builderId != null) {
        builders.push(toEntity('builder', response.builderId, response));
      }
      return;
    }

    const kind = toLocationKind(response.type);
    if (kind === undefined || response.id == null) {
      return;
    }

    locations.push({
      key: response.id.toString(),
      location: toLocation(response, kind, response.id),
    });
  });

  return { locations: locations.slice(0, locationSuggestionLimit), projects, builders };
}

function toEntity(kind: AutocompleteEntity['kind'], id: number, response: GetSearchAutocompleteItemResponse): AutocompleteEntity {
  return {
    kind,
    id,
    name: response.text,
    typeLabel: response.typeLabel,
  };
}

function toLocation(response: GetSearchAutocompleteItemResponse, kind: LocationKind, id: number): Location {
  return {
    id,
    name: response.text,
    kind,
    typeLabel: response.typeLabel,
    coordinates: {
      latitude: response.location.latitude,
      longitude: response.location.longitude,
    },
    cityId: response.cityId ?? undefined,
    regionId: response.regionId ?? undefined,
  };
}

function toLocationKind(value: string): LocationKind | undefined {
  if (value === 'city' || value === 'region' || value === 'location') {
    return value;
  }

  return undefined;
}

type ImageSource = { src: string; title: string };
type ProjectImageSource = {
  imagePriority: ProjectImagePriority;
  image?: ImageSource | null;
  images?: ImageSource[] | null;
  models: {
    image?: ImageSource | null;
    images?: ImageSource[] | null;
  }[];
};

const getProjectImage = (project: ProjectImageSource) => project.image ?? project.images?.[0] ?? null;
const getModelImage = (model: ProjectImageSource['models'][number]) => model.image ?? model.images?.[0] ?? null;

export function getImageByPriority(project: ProjectImageSource): { src: string; title: string } {
  if (project.imagePriority === 'project') {
    const projectImage = getProjectImage(project);
    if (projectImage) {
      return projectImage;
    }
    for (let i = 0; i < project.models.length; i++) {
      const model = project.models[i];
      if (!model) continue;
      const modelImage = getModelImage(model);
      if (modelImage) {
        return modelImage;
      }
    }
    return Constants.ProjectNoImageSrc;
  }

  for (let i = 0; i < project.models.length; i++) {
    const model = project.models[i];
    if (!model) continue;
    const modelImage = getModelImage(model);
    if (modelImage) {
      return modelImage;
    }
  }
  const projectImage = getProjectImage(project);
  if (projectImage) {
    return projectImage;
  }
  return Constants.ProjectModelNoImageSrc;
}

type ProjectPrice = {
  amount: number;
  isStarting: boolean;
  type: { id: number };
};

type ProjectCharacteristicsSource = {
  models: {
    price: ProjectPrice;
    bedrooms: number | null;
    bedroomsMax: number | null;
    bathrooms: number;
  }[];
};

export type BedroomsSummary = {
  hasStudio: boolean;
  min?: number;
  max?: number;
};

export function getMinPrice(project: ProjectCharacteristicsSource, priceTypeId?: number | null): ProjectPrice | undefined {
  const prices = project.models.filter((model) => model.price.amount > 0).map((model) => model.price);
  const forSalePrices = prices.filter((price) => price.type.id == Constants.PriceTypeId.ForSale).sort((a, b) => a.amount - b.amount);
  const forRentPrices = prices.filter((price) => price.type.id == Constants.PriceTypeId.ForRent).sort((a, b) => a.amount - b.amount);

  if (priceTypeId === Constants.PriceTypeId.ForSale) {
    return forSalePrices[0];
  }

  if (priceTypeId === Constants.PriceTypeId.ForRent) {
    return forRentPrices[0];
  }

  if (forSalePrices.length === 0 && forRentPrices.length === 0) {
    return undefined;
  }

  if (forRentPrices.length > forSalePrices.length) {
    return forRentPrices[0];
  }

  return forSalePrices[0];
}

function getRange(values: number[]): { min: number; max: number } | undefined {
  if (values.length === 0) {
    return undefined;
  }

  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

export function getBedroomsRange(project: ProjectCharacteristicsSource): BedroomsSummary | undefined {
  const values = project.models.flatMap((model) => [model.bedrooms, model.bedroomsMax]).filter((bedrooms): bedrooms is number => bedrooms !== null && bedrooms > 0);
  const hasStudio = project.models.some((model) => model.bedrooms === 0);
  const range = getRange(values);

  if (!hasStudio && !range) {
    return undefined;
  }

  return { hasStudio, min: range?.min, max: range?.max };
}

export function getBathroomsRange(project: ProjectCharacteristicsSource): { min: number; max: number } | undefined {
  return getRange(project.models.filter((model) => model.bathrooms > 0).map((model) => model.bathrooms));
}

export function getImagesByPriority(
  project: {
    imagePriority: ProjectImagePriority;
    images: { src: string; title: string }[];
    models: {
      images: { src: string; title: string }[];
    }[];
  },
  quantity: number
): { src: string; title: string }[] {
  const images: { src: string; title: string }[] = [];

  if (project.imagePriority === 'project') {
    // 1. Add all project images
    for (let i = 0; i < project.images.length && images.length < quantity; i++) {
      const projectImage = project.images[i];
      if (projectImage) images.push(projectImage);
    }
    // 2. Add all images from each model, in order
    for (let mi = 0; mi < project.models.length && images.length < quantity; mi++) {
      const model = project.models[mi];
      if (!model) continue;
      for (let ii = 0; ii < model.images.length && images.length < quantity; ii++) {
        const modelImage = model.images[ii];
        if (modelImage) images.push(modelImage);
      }
    }
    // 3. Fill with placeholders if needed
    while (images.length < quantity) {
      images.push(Constants.ProjectNoImageSrc);
    }
  } else {
    // 1. Add all images from each model, in order
    for (let mi = 0; mi < project.models.length && images.length < quantity; mi++) {
      const model = project.models[mi];
      if (!model) continue;
      for (let ii = 0; ii < model.images.length && images.length < quantity; ii++) {
        const modelImage = model.images[ii];
        if (modelImage) images.push(modelImage);
      }
    }
    // 2. Add all project images
    for (let i = 0; i < project.images.length && images.length < quantity; i++) {
      const projectImage = project.images[i];
      if (projectImage) images.push(projectImage);
    }
    // 3. Fill with placeholders if needed
    while (images.length < quantity) {
      images.push(Constants.ProjectModelNoImageSrc);
    }
  }

  return images;
}
